import type { ModuleInfo } from '../Bridge';
import { Bridge } from '../Bridge';
import { once } from '../utils';
import type { Version } from '../Version';

export type FetchResponse =
    | { type: 'none' }
    | {
          type: 'line';
          userID: string;
          accessToken: string;
          permissions: string[];
          profile?: UserProfile; // added in legacy-auth@0.1, optional for backward compatibility
      };

export type UserProfile = {
    displayName: string;
    pictureURL?: string;
    pictureURLLarge?: string;
    pictureURLSmall?: string;
    statusMessage?: string;
    userID: string;
};

export class LoginScreenModule {
    constructor(
        readonly bridge: Bridge,
        readonly module: ModuleInfo<'legacy-auth', 0>,
    ) {}

    private request<T>(data: any): Promise<T> {
        return this.bridge.request(this.module.id, data);
    }

    /**
     * Since legacy-auth@0.0
     */
    async fetch(): Promise<FetchResponse> {
        return this.request({ type: 'fetch' });
    }

    /**
     * Since legacy-auth@0.0
     */
    async logout(): Promise<void> {
        await this.request({ type: 'logout' });
    }
}

export class LoginScreen {
    /**
     * Creates a new instance using the provided bridge.
     */
    static create = (bridge: Bridge) => {
        const module = bridge.findModule('legacy-auth', 0);
        if (module) {
            return new LoginScreen(new LoginScreenModule(bridge, module), module.version);
        }
        return undefined;
    };

    /**
     * Returns a shared instance.
     */
    static getInstance = once(() => {
        const bridge = Bridge.getInstance();
        if (bridge) {
            return LoginScreen.create(bridge);
        }
        return undefined;
    });

    /** Fetch the current authentication state from the native app. */
    readonly fetch: () => Promise<FetchResponse>;

    /** Log out using the LINE SDK. */
    readonly logout: () => Promise<void>;

    constructor(
        readonly module: LoginScreenModule,
        readonly version: Version,
    ) {
        this.fetch = module.fetch.bind(module);
        this.logout = module.logout.bind(module);
    }
}
